import { useQuery, useQueryClient } from '@tanstack/react-query';
import { SourcingOpenings as SourcingOpeningsData } from 'api-clients/monolith';
import { useCallback, useEffect, useMemo } from 'react';

import { getDashboardOpenings } from '@/api-clients/get-dashboard-openings';
import { Pagination } from '@/components/base/pagination';
import { Search } from '@/components/base/search';
import { DataTable } from '@/components/ui/data-table';
import { EditTargetsProvider } from '@/contexts/edit-targets-context';
import { NoHiringGoalDialogProvider } from '@/contexts/no-hiring-goal-dialog-context';
import { TableProvider, useTableContext } from '@/contexts/table-context';
import { useDataTable } from '@/hooks/use-data-table';
import { queryKeys } from '@/query-keys';

import { Filters } from '../filters';
import { useDashboardColumns } from './columns';
import { NoOpenings } from './no-openings';
import { RowsSelect } from './rows-select';

type DashboardTableProps = {
  showAggregateData: boolean;
  setShowAggregateData: (showAggregateData: boolean) => void;
};

function DashboardTableContent({
  showAggregateData,
  setShowAggregateData,
}: DashboardTableProps) {
  const {
    pagination: { pageIndex, pageSize },
    rowCount,
    setRowCount,
    searchQuery,
    setSearchQuery,
    filters: { positionIds, locationIds, brandIds },
    sorting,
  } = useTableContext();
  const sort = sorting[0];

  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: [
      ...queryKeys.dashboard.openings,
      pageIndex,
      searchQuery,
      positionIds,
      locationIds,
      showAggregateData,
      brandIds,
      sorting,
    ],
    queryFn: () =>
      getDashboardOpenings({
        selectedForSourcing: true,
        page: pageIndex + 1,
        perPage: pageSize,
        query: searchQuery,
        positionIds,
        locationIds,
        showAggregateData,
        brandIds,
        sortBy: sort?.id,
        sortDirection: sort?.desc ? 'desc' : 'asc',
      }),
  });

  if (isError) {
    throw new Error('Error fetching dashboard openings');
  }

  // invalidateQueries: callback invoked by RowsSelect on mutation success (cross-component)
  const refetchOpenings = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: queryKeys.dashboard.openings,
    });
  }, [queryClient]);

  const tableRows = useMemo(() => data?.data ?? [], [data]);
  const defaultMetricsPeriod = useMemo((): number | null | undefined => {
    if (data === undefined) return undefined;
    return data.default_metrics_period ?? null;
  }, [data]);

  const columns = useDashboardColumns(defaultMetricsPeriod);

  useEffect(() => {
    setRowCount(data?.pagination?.total_count ?? 0);
  }, [data, setRowCount]);

  const { table } = useDataTable<SourcingOpeningsData>({
    columns,
    data: tableRows,
    rowCount,
    externalIdKey: 'external_id',
  });

  return (
    <>
      <div className="flex justify-between gap-2">
        <Filters />
        <Search setSearchQuery={setSearchQuery} />
      </div>
      <DataTable
        table={table}
        columns={columns}
        isLoading={isLoading}
        emptyState={<NoOpenings />}
        showAggregateData={showAggregateData}
        setShowAggregateData={setShowAggregateData}
      />
      {tableRows.length > 0 && <Pagination table={table} />}
      <RowsSelect table={table} refetchOpenings={refetchOpenings} />
    </>
  );
}

export function DashboardTable({
  showAggregateData,
  setShowAggregateData,
}: DashboardTableProps) {
  return (
    <NoHiringGoalDialogProvider>
      <EditTargetsProvider>
        <TableProvider>
          <DashboardTableContent
            showAggregateData={showAggregateData}
            setShowAggregateData={setShowAggregateData}
          />
        </TableProvider>
      </EditTargetsProvider>
    </NoHiringGoalDialogProvider>
  );
}
